import React, { useMemo, useState, useEffect } from 'react';
import { useListActivities, Activity } from '../../../../api/activities';
import DateWithTooltip from '../../../common/DateWithTooltip';
import Badge from '../../../common/Badge';
import { Image } from '../../../ui/Image';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ExternalLink } from '../../../common/ExternalLink';
import { Table } from '@plerionhq/design-system/experimental';
import type { ColumnDef } from '@tanstack/react-table';
import { useFilters } from '@plerionhq/design-system';
import type { Filter } from '@plerionhq/design-system';
import './ActivityList.css';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

export enum ActivityStatus {
  Pending = 'pending',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

interface ActivityListProps {
  sourceId?: string;
  itemsPerPage?: number;
}

// Status filter options mapping display labels to API values
const statusOptions = (t: TFunction<'pleri/chat'>) =>
  [
    { label: t('open'), value: 'pending' },
    { label: t('completedMerged'), value: 'completed' },
    { label: t('closed'), value: 'cancelled' },
  ] as const;

// Inner component that uses filters
const ActivityListContent: React.FC<{
  sourceId?: string;
  itemsPerPage: number;
}> = ({ sourceId, itemsPerPage }) => {
  const { t } = useTranslation('pleri/chat');
  const [pageIndex, setPageIndex] = useState(0);

  // Define filters configuration for useFilters
  const filtersConfig = useMemo(
    () =>
      [
        {
          id: 'status',
          label: t('common:status'),
          type: 'multiSelect',
          options: statusOptions(t).map((opt) => ({
            label: opt.label,
            value: opt.value,
          })),
          showInlineClear: true,
          permanent: true,
        },
        {
          id: 'createdAt',
          label: t('created'),
          type: 'dateRange',
          placeholder: t('selectCreatedDateRange'),
          permanent: true,
          showInlineClear: true,
          startKey: 'createdAtStart',
          endKey: 'createdAtEnd',
        },
      ] as Filter[],
    [t],
  );

  const { filterValues, filtersRender } = useFilters({
    filters: filtersConfig,
    mode: 'state',
  });
  const offset = pageIndex * itemsPerPage;

  // Get filter values from useFilters hook
  const statusFilterValue = useMemo(
    () => (filterValues.status as (string | number)[]) || [],
    [filterValues.status],
  );

  // Reset page index when filters change
  useEffect(() => {
    setPageIndex(0);
  }, [filterValues.status, filterValues.createdAt]);

  // Map selected status values to unique API status values
  const statusApiValues = useMemo(() => {
    const uniqueValues = new Set<string>();
    statusFilterValue.forEach((value) => {
      const option = statusOptions(t).find((opt) => opt.value === value);
      if (option) {
        uniqueValues.add(option.value);
      }
    });
    return Array.from(uniqueValues);
  }, [statusFilterValue]);

  // Build createdAt filter object from date range
  const createdAtFilter = useMemo(() => {
    const filter: { start?: string; end?: string } = {};
    if (filterValues.createdAtStart) {
      const startDate = new Date(filterValues.createdAtStart as string);
      startDate.setHours(0, 0, 0, 0);
      filter.start = startDate.toISOString();
    }

    if (filterValues.createdAtEnd) {
      const endDate = new Date(filterValues.createdAtEnd as string);
      endDate.setHours(23, 59, 59, 999);
      filter.end = endDate.toISOString();
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [filterValues.createdAtEnd, filterValues.createdAtStart]);

  const {
    data: response,
    isLoading,
    error,
  } = useListActivities({
    sourceId,
    limit: itemsPerPage,
    offset,
    type: ['CODE_REVIEW', 'JIRA'],
    status: statusApiValues,
    createdAt: createdAtFilter,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Define columns for Table (must be before early returns for React hooks rules)
  const columns: ColumnDef<Activity>[] = useMemo(
    () => [
      {
        id: 'activity',
        header: t('activity'),
        cell: ({ row }) => {
          const activity = row.original;
          const isCodeReview = activity.type === 'CODE_REVIEW';
          const isJira = activity.type === 'JIRA';
          const isCodeFix = activity.type === 'CODE_FIX';
          const activityType = getActivityType(activity);
          const extractedInfo =
            (isCodeReview || isJira) && activity.reference
              ? activityType.extractInfo(activity.reference)
              : null;

          return (
            <div className="flex items-center gap-2">
              {activityType.icon && !isCodeFix && (
                <Image
                  src={activityType.icon}
                  alt={activityType.alt}
                  width={16}
                  height={16}
                  className="flex-shrink-0 rounded"
                />
              )}
              {/* Code Review and Jira specific display */}
              {(isCodeReview || isJira) && activity.reference ? (
                <ExternalLink href={activity.reference} showExternalIcon>
                  <span>
                    {extractedInfo
                      ? extractedInfo.title
                      : activityType.linkText}
                  </span>
                </ExternalLink>
              ) : isCodeFix ? (
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('createChangeRequest')}
                </span>
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  —
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'status',
        header: t('common:status'),
        accessorKey: 'status',
        cell: ({ row }) => {
          const activity = row.original;
          const isJira = activity.type === 'JIRA';
          if (isJira) {
            return <span className="text-gray-400 dark:text-gray-500">—</span>;
          }
          return (
            <Badge variant={getStatusBadgeVariant(activity.status)}>
              {getStatusDisplayText({
                status: activity.status,
                activityType: activity.type,
                ciFailed: activity?.meta?.ciStatus === 'Failed',
              })}
            </Badge>
          );
        },
      },
      {
        id: 'createdAt',
        header: t('createAt'),
        accessorKey: 'createdAt',
        cell: ({ row }) => <DateWithTooltip date={row.original.createdAt} />,
      },
    ],
    [t],
  );

  const activities = response?.data || [];
  const totalCount = response?.meta?.total || 0;

  // Filter out CODE_FIX activities with "completed" status
  const filteredActivities = activities.filter((activity) => {
    if (
      activity.type === 'CODE_FIX' &&
      activity.status.toLowerCase() === 'completed'
    ) {
      return false;
    }
    return true;
  });

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;

  const handlePreviousPage = () => {
    if (canPreviousPage) {
      setPageIndex((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (canNextPage) {
      setPageIndex((prev) => prev + 1);
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex w-full justify-end">
        <div className="flex flex-row gap-2 p-2 pb-4">{filtersRender}</div>
      </div>
      {error && (
        <div className="p-md text-negative-base">
          <p>{t('errorLoadingActivities')}</p>
        </div>
      )}
      <Table
        data={filteredActivities}
        columns={columns}
        isLoading={isLoading}
      />
      {/* Server-side pagination controls */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {t('showingFromToOfResults', {
              from: offset + 1,
              to: Math.min(offset + itemsPerPage, totalCount),
              total: totalCount,
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePreviousPage}
            disabled={!canPreviousPage}
            className="rounded border border-gray-200 bg-white px-2 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('pageOf', { page: pageIndex + 1, total: totalPages })}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!canNextPage}
            className="rounded border border-gray-200 bg-white px-2 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const ActivityList: React.FC<ActivityListProps> = ({
  sourceId,
  itemsPerPage = 25,
}) => {
  return (
    <ActivityListContent sourceId={sourceId} itemsPerPage={itemsPerPage} />
  );
};

// Activity type configuration
interface ActivityTypeConfig {
  type: 'github' | 'gitlab' | 'bitbucket' | 'jira' | 'other';
  icon: string;
  alt: string;
  extractInfo: (
    url: string,
  ) => { title: string; owner: string; repo: string; number: string } | null;
  linkText: string;
}

// Helper function to determine activity type and get configuration
const getActivityType = (activity: Activity): ActivityTypeConfig => {
  if (!activity.reference) {
    return {
      type: 'other',
      icon: '',
      alt: '',
      extractInfo: () => null,
      linkText: 'View',
    };
  }

  try {
    const url = new URL(activity.reference);

    // Jira configuration
    if (activity.type === 'JIRA') {
      return {
        type: 'jira',
        icon: 'logos/jira.svg',
        alt: 'Jira',
        extractInfo: (url: string) => {
          const match = url.match(/\/browse\/([A-Z]+-\d+)/);
          if (match) {
            const [, issueKey] = match;
            return {
              title: issueKey,
              owner: '',
              repo: '',
              number: issueKey,
            };
          }
          return null;
        },
        linkText: 'View Issue',
      };
    }

    // GitHub configuration
    if (url.hostname === 'github.com' || url.hostname.endsWith('.github.com')) {
      return {
        type: 'github',
        icon: 'resourceType/github-secret.png',
        alt: 'GitHub',
        extractInfo: (url: string) => {
          const match = url.match(/github\.com\/([^/]+)\/([^/]+)\/pull\/(\d+)/);
          if (match) {
            const [, owner, repo, number] = match;
            return {
              title: `${owner}/${repo}#${number}`,
              owner,
              repo,
              number,
            };
          }
          return null;
        },
        linkText: 'View PR',
      };
    }

    // GitLab configuration
    if (url.hostname === 'gitlab.com' || url.hostname.endsWith('.gitlab.com')) {
      return {
        type: 'gitlab',
        icon: 'resourceType/gitlab-secret.png',
        alt: 'GitLab',
        extractInfo: (url: string) => {
          const match = url.match(
            /gitlab\.com\/([^/]+)\/([^/]+)\/-\/merge_requests\/(\d+)/,
          );
          if (match) {
            const [, owner, repo, number] = match;
            return {
              title: `${owner}/${repo}!${number}`,
              owner,
              repo,
              number,
            };
          }
          return null;
        },
        linkText: 'View MR',
      };
    }

    // Bitbucket configuration
    if (
      url.hostname === 'bitbucket.org' ||
      url.hostname.endsWith('.bitbucket.org')
    ) {
      return {
        type: 'bitbucket',
        icon: 'resourceType/bitbucket-secret.png',
        alt: 'Bitbucket',
        extractInfo: (url: string) => {
          const match = url.match(
            /bitbucket\.org\/([^/]+)\/([^/]+)\/pull-requests\/(\d+)/,
          );
          if (match) {
            const [, owner, repo, number] = match;
            return {
              title: `${owner}/${repo}!${number}`,
              owner,
              repo,
              number,
            };
          }
          return null;
        },
        linkText: 'View PR',
      };
    }
  } catch {
    // If parsing fails, fallback to other
  }

  // Default configuration for other types
  return {
    type: 'other',
    icon: '',
    alt: '',
    extractInfo: () => null,
    linkText: 'View',
  };
};

// Helper function to get status badge variant
const getStatusBadgeVariant = (
  status: string,
): 'default' | 'success' | 'warning' | 'error' => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function to get display text for status
function getStatusDisplayText({
  status,
  activityType,
  ciFailed,
}: {
  status: string;
  activityType: string;
  ciFailed?: boolean;
}): string {
  switch (status.toLowerCase()) {
    case 'completed':
      // If activity type is CODE_FIX, return Completed,
      // If activity type is CODE_REVIEW, return Merged
      return activityType === 'CODE_REVIEW' ? 'Merged' : 'Completed';
    case 'cancelled':
      // If activity type is CODE_FIX, return Failed,
      // If activity type is CODE_REVIEW, return Closed
      return activityType === 'CODE_REVIEW' ? 'Closed (Rejected)' : 'Failed';
    case 'pending':
      return ciFailed ? 'Open (Failed CI)' : 'Open';
    default:
      return status;
  }
}
